import type { ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { ChevronRight, SellOutlined } from "@mui/icons-material";
import { BannerCloseButton, BannerCta } from "./BannerParts";
import { BannerCountdown } from "./PromoParts";
import {
  BANNER_VARIANTS,
  bannerSurfaceSx,
  type BannerIconVisual,
  type BannerVariant,
} from "./bannerVariants";

type TopBarBannerProps = {
  text: string;
  /** A promo code, printed in bold after the text. */
  code?: string;
  /** Seconds left on the countdown, shown after the text. */
  countdown?: number;
  /** No CTA once the offer is disabled. */
  ctaLabel?: string;
  onCtaClick: () => void;
  onClose?: () => void;
  variant: BannerVariant;
  /** Mobile never shows it. */
  visual?: BannerIconVisual;
};

/** One-line bar above the page title. Presentation only. */
export const TopBarBanner = ({
  text,
  code,
  countdown,
  ctaLabel,
  onCtaClick,
  onClose,
  variant,
  visual,
}: TopBarBannerProps) => {
  const tokens = BANNER_VARIANTS[variant];

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      px={2}
      py={{ xs: 1, md: 2 }}
      sx={bannerSurfaceSx(variant)}
      data-testid="top-bar-banner"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={{ xs: 2, md: 3 }}
        flex={1}
        minWidth={0}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={{ xs: 1, md: 3 }}
          flex={{ xs: 1, md: "initial" }}
          minWidth={0}
        >
          <Stack direction="row" alignItems="center" spacing={1} useFlexGap>
            {visual && (
              <TopBarIcon icon={visual.icon} color={tokens.accentColor} />
            )}
            <Typography
              variant="body1"
              color={tokens.titleColor}
              sx={{ typography: { xs: "body2", md: "body1" } }}
            >
              {text}
              {code && (
                <>
                  {" "}
                  <Box component="strong" typography="body1" fontWeight="bold">
                    {code}
                  </Box>
                </>
              )}
            </Typography>
          </Stack>
          {countdown !== undefined && (
            <BannerCountdown
              seconds={countdown}
              tone={tokens.iconTone}
              look="compact"
            />
          )}
        </Stack>
        {ctaLabel && (
          <BannerCta
            label={ctaLabel}
            onClick={onCtaClick}
            variant={variant}
            size="small"
            endIcon={<ChevronRight />}
            sx={{
              py: 0.5,
              "& .MuiButton-endIcon": { display: { xs: "none", md: "flex" } },
            }}
          />
        )}
      </Stack>
      {onClose && <BannerCloseButton onClose={onClose} variant={variant} />}
    </Stack>
  );
};

const TopBarIcon = ({ icon, color }: { icon?: ReactNode; color: string }) => (
  <Box
    display={{ xs: "none", md: "flex" }}
    color={color}
    sx={{ "& svg": { fontSize: 16 } }}
  >
    {icon ?? <SellOutlined />}
  </Box>
);
